import { QueryClient, useQuery, useQueryClient } from '@tanstack/react-query';
import { CSSProperties } from 'react';

import API from '@api/API';

import { EntertainerScheduleDateDetail } from '@screens/entertainer/DragKingCommunityScreen/ScheduledSocialPost';
import { EntertainerMetricsCardProps } from '@screens/entertainer/EntertainerMetricsScreen/EntertainerMetrics.types';

import { useAppContext } from '../contexts/AppContext';
import { AppStorageKey, useAppStorageValue } from '../utilities/appStorage';
import { guessTimezone } from '../utilities/guessTimezone';
import {
  BasicRating,
  CritiqueData,
  CritiqueQuestion,
  EntertainerBadge,
  UserCritique,
} from './CritiqueQueries';
import { FeedEvent } from './EventQueries';
import { FeedPost } from './PostQueries';
import { PublicBookingOpportunity, useCityBookingOpportunitiesQuery } from './PromoterQueries';
import { SplashNotification } from './UserQueries';

export const TYPES_RECORD = {
  dragPerformer: 'Drag Performer',
  dj: 'DJ',
  gogo: 'Go-Go',
  comedian: 'Comedian',
  other: 'Other',
} as const;

export type EntertainerType = keyof typeof TYPES_RECORD;

export interface EntertainerSocialLink {
  type: string;
  url: string;
  text: string;
}

export interface Entertainer {
  _id: string;
  lastCheckedAt?: string;
  avatar: string;
  avatarId?: string;
  badgeCount: number;
  likeCount: number;
  commentCount: number;
  unviewedCommentCount: number;
  trendingScore?: number;
  title: string;
  tagline: string;
  description: string;
  type: EntertainerType;
  cityIds: string[];
  cities: { _id: string; name: string }[];
  details: any;
  tags: {
    like: string[];
    neutral: string[];
    dislike: string[];
  };
  bookingHandle?: string;
  smsKeyword?: string;
  availabilityReferralUrl?: string;
  sweepstakesReferralUrl?: string;
  contact?: {
    email?: string;
    instagram?: string;
    facebook?: string;
    venmo?: string;
    cashApp?: string;
    links?: EntertainerSocialLink[];
  };
  featuredMedia: {
    _id: string;
    title: string;
    image: string;
    meta: {
      photographerName?: string;
      photographerInstagramHandle?: string;
    };
  }[];
  isFeaturedBipocArtist?: boolean;
  featuredCtas?: EntertainerFeaturedCta[];
}

export interface CurrentEntertainer extends Entertainer {
  boostExpiresAt?: string;
  boosts?: string[];
  hasFlexibleAvailability: boolean;
}
export interface EntertainerBookingOpportunity extends Pick<
  PublicBookingOpportunity,
  'type' | 'messaging'
> {
  primaryImage?: string;
  _id: string;
  title: string;
  details: string;
  startDate: string;
  startTime: string;
  city: { name: string };
  venue: { title: string };
  promoter: { _id: string; title: string };
  status: PublicBookingOpportunity['entertainers'][0]['status'] | 'new';
  updates: PublicBookingOpportunity['updates'];
  askedDetailQuestions: {
    _id: string;
    detailQuestionId: string;
    question: string;
    answer?: string;
  }[];
}

export interface EntertainerBookingRequest {
  _id: string;
  status: string;
  requestedBy: string;
  createdAt: string;
  city: {
    _id: string;
    name: string;
  };
  entertainer: {
    _id: string;
    title: string;
  };
  details: {
    startDate: string;
    startTime: string;
    venue: { title: string };
  };
  contact: {
    name: string;
    phone: string;
    email: string;
  };
  performance: {
    duration: string;
    type: string;
    specialRequests: string;
    additionalQuestions?: string;
  };
  technical: {
    soundSystem: string;
    dressingRoom: string;
    lightingSetup: string;
  };
}

export interface EntertainerCompetition {
  _id: string;
  title: string;
  subtitle?: string;
  startDate: string;
  endDate: string;
  instructions?: string;
  additionalTips?: string[];
  frameOptions?: ('gaggl' | 'cdr-default' | 'cdr-s3')[];
  toSupportMessage?: string;
}

export const useCurrentEntertainerQuery = (
  options: {
    isEnabled?: boolean;
    refetchOnWindowFocus?: boolean;
    refetchOnReconnect?: boolean;
    refetchOnMount?: boolean;
  } = {},
) => {
  const { isEnabled = true } = options;
  const { userIsLoggedIn } = useAppContext();

  const storedEntertainer = useAppStorageValue(AppStorageKey.Entertainer);

  const {
    isFetched: entertainerIsReady,
    data,
    refetch: refetchEntertainer,
  } = useQuery({
    queryKey: useCurrentEntertainerQuery.queryKey(),
    queryFn: () => useCurrentEntertainerQuery.queryFn(),
    enabled: userIsLoggedIn && isEnabled,
    ...options,
  });

  return {
    entertainerIsReady,
    hasOnboardedBookingWizard: data?.hasOnboardedBookingWizard,
    entertainer: data?.entertainer ?? storedEntertainer,
    bookingOpportunities: data?.bookingOpportunities,
    bookingRequests: data?.bookingRequests,
    upcomingEvents: data?.upcomingEvents,
    competitions: data?.competitions,
    eventRequests: data?.eventRequests,
    basicRatings: data?.basicRatings ?? { like: 0, neutral: 0, dislike: 0 },
    entertainerMetricsSection: data?.metricsSection,
    upcomingScheduleDateDetails: data?.upcomingScheduleDateDetails,
    entertainerEventReferralOpportunities: data?.eventReferralOpportunities,
    refetchEntertainer,
  };
};
useCurrentEntertainerQuery.queryKey = () => ['user', 'entertainer'];
useCurrentEntertainerQuery.queryFn = async () => {
  const response = await API.get<{
    hasOnboardedBookingWizard: boolean;
    entertainer: CurrentEntertainer;
    bookingOpportunities: EntertainerBookingOpportunity[];
    bookingRequests: EntertainerBookingRequest[];
    upcomingEvents: (FeedEvent & { canUserEdit: boolean })[];
    competitions: EntertainerCompetition[];
    eventRequests: FeedEvent[];
    basicRatings: {
      like: number;
      neutral: number;
      dislike: number;
    };
    metricsSection: {
      title: string;
      content: {
        label: string;
        value: number;
        badgeCount: number;
        trendDirection?: 'up' | 'down';
        hideValue?: boolean;
      }[];
    };
    upcomingScheduleDateDetails: EntertainerScheduleDateDetail[];
    eventReferralOpportunities: {
      _id: string;
      title: string;
      startDate: string;
      referralLink?: string;
      referrals: { createdAt: string; entertainer: { _id: string; title: string } }[];
    }[];
  }>(`/v1/entertainer/me`);

  if (response?.entertainer) {
    localStorage.setItem(AppStorageKey.Entertainer, JSON.stringify(response.entertainer));
  } else {
    localStorage.removeItem(AppStorageKey.Entertainer);
  }

  return response;
};

export const fetchCurrentEntertainer = async (queryClient: QueryClient) => {
  const queryKey = useCurrentEntertainerQuery.queryKey();
  await Promise.all([
    queryClient.cancelQueries({ queryKey }),
    queryClient.invalidateQueries({ queryKey }),
  ]);

  return queryClient.fetchQuery({
    queryKey: useCurrentEntertainerQuery.queryKey(),
    queryFn: () => useCurrentEntertainerQuery.queryFn(),
  });
};

export const useFetchCurrentEntertainer = () => {
  const queryClient = useQueryClient();

  return () => fetchCurrentEntertainer(queryClient);
};

export const useCurrentEntertainerMetricsQuery = (query?: Record<string, string>) => {
  const { isFetched, data, refetch } = useQuery({
    queryKey: useCurrentEntertainerMetricsQuery.queryKey(query),
    queryFn: () => useCurrentEntertainerMetricsQuery.queryFn(query),
    enabled: true,
  });

  return {
    entertainerMetricsAreReady: isFetched,
    entertainerMetrics: data,
    refetchEntertainerMetrics: refetch,
  };
};

interface EntertainerMetricsHeader {
  title: string;
  description: string;
}

useCurrentEntertainerMetricsQuery.queryKey = (query) => ['entertainer-metrics', query];
useCurrentEntertainerMetricsQuery.queryFn = (query) => {
  const queryParams = new URLSearchParams(query).toString();

  return API.get<{
    isEnabled: boolean;
    metricsSection: {
      indicatorCount?: number;
      title: string;
      content: {
        style?: CSSProperties;
        label: string;
        value: number;
        badgeCount: number;
        trendDirection?: 'up' | 'down';
        hideValue?: boolean;
      }[];
    };
    metricsDetails: {
      header: EntertainerMetricsHeader;
      content: EntertainerMetricsCardProps[];
    };
  }>(`/v1/user/entertainer/metrics${queryParams ? `?${queryParams}` : ''}`);
};

export interface EntertainerFeaturedCta {
  img: string;
  title: string;
  description: string;
  url: string;
}

export interface EntertainerLink {
  type: string;
  url: string;
  text: string;
}

export type ProfileLink = EntertainerLink;
export type ProfileContact = {
  instagram?: string;
  venmo?: string;
  tiktok?: string;
  email?: string;
  website?: string;
  links?: ProfileLink[];
};

export interface UnauthorizedPublicEntertainer {
  _id: string;
  isPublic?: boolean;
  isDemoEnabled?: {
    primaryCommentDisplayName: string;
    primaryCommentText: string;
  };
  isClaimed: boolean;
  avatar?: string;
  title: string;
  tagline?: string;
  description?: string;
  tags?: {
    like: string[];
    neutral: string[];
    dislike: string[];
  };
  type?: string;
  cities: { _id: string; name: string; rank: number; total: number }[];
  contact?: ProfileContact;
  details: {
    categories: string[];
  };
  featuredMedia: {
    _id: string;
    title: string;
    image: string;
    videoUrl?: string;
    type: 'image' | 'video';
  }[];
  bookingHandle?: string;
  isFeaturedBipocArtist?: boolean;
  isFeaturedKingThing?: boolean;
  featuredCtas?: EntertainerFeaturedCta[];
}

export interface PersonalizedBadge {
  _id: string;
  title: string;
  imgUrl?: string;
  svgText?: string;
  description: string;
  slug: string;
}

export const useEntertainerByBookingHandleQuery = (
  bookingHandle: string,
): {
  entertainerIsReady: boolean;
  entertainer?: UnauthorizedPublicEntertainer | undefined;
  upcomingEntertainerEvents?: FeedEvent[];
  sharedEntertainerEvents?: FeedEvent[];
  hypedEntertainerEvents?: FeedEvent[];
  upcomingEntertainerScheduleDateDetails?: EntertainerScheduleDateDetail[];
  activeEntertainerCompetitions?: { _id: string; title: string }[];
  personalizedBadge?: PersonalizedBadge;
  refetchEntertainer: () => Promise<void>;
} => {
  const { isFetched, data, refetch } = useQuery({
    queryKey: ['entertainer', 'bookingHandle', bookingHandle],
    queryFn: () =>
      API.get<{
        entertainer: UnauthorizedPublicEntertainer;
        upcomingEvents: FeedEvent[];
        sharedEvents: FeedEvent[];
        activeCompetitions: { _id: string; title: string }[];
        personalizedBadge?: PersonalizedBadge;
        upcomingScheduleDateDetails?: EntertainerScheduleDateDetail[];
      }>(`/v1/entertainers/bookingHandle/${bookingHandle}`),
    enabled: !!bookingHandle,
  });

  return {
    entertainerIsReady: isFetched,
    entertainer: data?.entertainer,
    upcomingEntertainerEvents: data?.upcomingEvents,
    sharedEntertainerEvents: data?.sharedEvents,
    upcomingEntertainerScheduleDateDetails: data?.upcomingScheduleDateDetails,
    hypedEntertainerEvents: data?.sharedEvents?.filter((event) => event.isHyped),
    activeEntertainerCompetitions: data?.activeCompetitions,
    personalizedBadge: data?.personalizedBadge,
    refetchEntertainer: async () => {
      await refetch();
    },
  };
};

export const useRelatedEntertainersByBookingHandleQuery = (
  bookingHandle: string,
  isEnabled = true,
) => {
  const { isFetched, data } = useQuery({
    queryKey: ['entertainer', 'bookingHandle', bookingHandle, 'related-entertainers'],
    queryFn: () =>
      API.get<{
        relatedEntertainers: Entertainer[];
      }>(`/v1/entertainers/bookingHandle/${bookingHandle}/related-entertainers`),
    enabled: !!bookingHandle && isEnabled,
  });

  return {
    relatedEntertainersAreReady: isFetched,
    relatedEntertainers: data?.relatedEntertainers,
  };
};

export const useEntertainerByIdQuery = (entertainerId: string) => {
  const { isFetched, data } = useQuery({
    queryKey: useEntertainerByIdQuery.queryKey(entertainerId),
    queryFn: () => useEntertainerByIdQuery.queryFn(entertainerId),
    enabled: !!entertainerId,
  });

  return {
    entertainerIsReady: isFetched,
    entertainer: data?.entertainer,
  };
};
useEntertainerByIdQuery.queryKey = (entertainerId: string) => ['entertainer', entertainerId];
useEntertainerByIdQuery.queryFn = (entertainerId: string) =>
  API.get<{
    entertainer: UnauthorizedPublicEntertainer;
  }>(`/v1/entertainers/${entertainerId}`);

export const useEntertainerReviewsQuery = (
  entertainerId?: string,
  {
    isEnabled = true,
    ...options
  }: {
    isEnabled?: boolean;
    refetchOnWindowFocus?: boolean;
    refetchOnReconnect?: boolean;
    refetchOnMount?: boolean;
  } = {},
) => {
  const { isFetched, data, refetch } = useQuery({
    queryKey: useEntertainerReviewsQuery.queryKey(entertainerId),
    queryFn: () => useEntertainerReviewsQuery.queryFn(entertainerId),
    enabled: !!entertainerId && isEnabled,
    ...options,
  });

  return {
    reviewsAreReady: isFetched,
    userPrimaryRating: data?.userPrimaryRating,
    reviews: data?.reviews,
    refetchReviews: refetch,
  };
};
useEntertainerReviewsQuery.queryKey = (entertainerId: string) => [
  'entertainer-reviews',
  entertainerId,
];
useEntertainerReviewsQuery.queryFn = (entertainerId: string) =>
  API.get<{
    userPrimaryRating?: BasicRating;
    reviews: FeedPost[];
  }>(`/v1/entertainers/${entertainerId}/reviews`);

export interface EntertainerRank {
  _id: string;
  title: string;
  rank: number;
  bookingHandle: string;
  tags: {
    like: string[];
    neutral: string[];
    dislike: string[];
  };
  primaryRating: string;
  like: { label: string }[];
  neutral: { label: string }[];
  dislike: { label: string }[];
  details?: { categories: string[] };
}
export const useTopEntertainersQuery = (cityId: string, type: string, userIsLoggedIn: boolean) => {
  const { isFetched, data, refetch } = useQuery({
    queryKey: useTopEntertainersQuery.queryKey(cityId, type, userIsLoggedIn),
    queryFn: () => useTopEntertainersQuery.queryFn(cityId, type),
    enabled: !!cityId && !!type,
  });

  return {
    topEntertainersAreReady: isFetched,
    totalEntertainers: data?.total,
    topEntertainers: data?.entertainers,
    refetchTopEntertainers: refetch,
  };
};
useTopEntertainersQuery.queryKey = (cityId: string, type: string, userIsLoggedIn: boolean) => [
  'top-entertainers',
  type,
  cityId,
  userIsLoggedIn,
];
useTopEntertainersQuery.queryFn = (cityId: string, type: string) => {
  return cityId !== 'other'
    ? API.get<{
        total: number;
        entertainers: EntertainerRank[];
      }>(`/v1/entertainers/top?cityId=${cityId}&type=${type}`)
    : Promise.resolve({
        total: 0,
        entertainers: [],
      });
};

export const useHydratedEntertainersQuery = (
  cityId: string,
  type: string,
  {
    isEnabled = true,
    ...options
  }: {
    isEnabled?: boolean;
    refetchOnWindowFocus?: boolean;
    refetchOnReconnect?: boolean;
    refetchOnMount?: boolean;
  } = {},
) => {
  const { isFetched, data, refetch } = useQuery({
    queryKey: useHydratedEntertainersQuery.queryKey(cityId, type),
    queryFn: () => useHydratedEntertainersQuery.queryFn(cityId, type),
    enabled: !!cityId && !!type && isEnabled,
    ...options,
  });

  return {
    entertainersAreReady: isFetched,
    entertainers: data?.entertainers ?? {},
    refetchTopEntertainers: refetch,
  };
};
useHydratedEntertainersQuery.queryKey = (cityId: string, type: string) => [
  'hydrated-entertainers',
  type,
  cityId,
];
useHydratedEntertainersQuery.queryFn = (cityId: string, type: string) =>
  API.get<{
    entertainers: Record<string, Entertainer>;
  }>(`/v1/cities/${cityId}/entertainers/?type=${type}`);

export const useRecommendedEntertainersQuery = (cityId: string, type: string) => {
  const { isFetched, data, refetch } = useQuery({
    queryKey: useRecommendedEntertainersQuery.queryKey(cityId, type),
    queryFn: () => useRecommendedEntertainersQuery.queryFn(cityId, type),
    enabled: !!cityId && !!type,
  });

  return {
    recommendedEntertainersAreReady: isFetched,
    recommendedEntertainers: data?.entertainerIdsByQuestionSlug,
    refetchRecommendedEntertainers: refetch,
  };
};

useRecommendedEntertainersQuery.queryKey = (cityId: string, type: string) => [
  'recommended-entertainers',
  type,
  cityId,
];

useRecommendedEntertainersQuery.queryFn = (cityId: string, type: string) =>
  API.get<{
    entertainerIdsByQuestionSlug: Record<string, string[]>;
  }>(`/v1/entertainers/recommended?cityId=${cityId}&type=${type}`);

export const usePrioritizedEntertainersQuery = (cityId: string, type: string, isEnabled = true) => {
  const { isFetched, data, refetch } = useQuery({
    queryKey: usePrioritizedEntertainersQuery.queryKey(cityId, type),
    queryFn: () => usePrioritizedEntertainersQuery.queryFn(cityId, type),
    enabled: !!cityId && !!type && isEnabled,
  });

  return {
    prioritizedEntertainersAreReady: isFetched,
    prioritizedEntertainers: data?.entertainers,
    refetchprioritizedEntertainers: refetch,
  };
};

usePrioritizedEntertainersQuery.queryKey = (cityId: string, type: string) => [
  'prioritized-entertainers',
  type,
  cityId,
];
usePrioritizedEntertainersQuery.queryFn = (cityId: string, type: string) =>
  API.get<{
    entertainers: { _id: string; title: string; primaryRating }[];
  }>(`/v1/entertainers/prioritized?cityId=${cityId}&type=${type}`);

export const useFeaturedEntertainersQuery = (cityId: string, type: string) => {
  const { isFetched, data, refetch } = useQuery({
    queryKey: useFeaturedEntertainersQuery.queryKey(cityId, type),
    queryFn: () => useFeaturedEntertainersQuery.queryFn(cityId, type),
    enabled: !!cityId && !!type,
  });

  return {
    featuredEntertainersAreReady: isFetched,
    featuredEntertainers: data?.entertainers,
    refetchFeaturedEntertainers: refetch,
  };
};

useFeaturedEntertainersQuery.queryKey = (cityId: string, type: string) => [
  'featured-entertainers',
  type,
  cityId,
];
useFeaturedEntertainersQuery.queryFn = (cityId: string, type: string) =>
  API.get<{
    entertainers: Entertainer[];
  }>(`/v1/entertainers/featured?cityId=${cityId}&type=${type}`);

export const useEntertainerCritiqueDataQuery = (entertainerId: string, isEnabled = true) => {
  const { isFetched, data, refetch } = useQuery({
    queryKey: useEntertainerCritiqueDataQuery.queryKey(entertainerId),
    queryFn: () => useEntertainerCritiqueDataQuery.queryFn(entertainerId),
    enabled: isEnabled && !!entertainerId,
    refetchOnMount: 'always',
    refetchOnReconnect: 'always',
    refetchOnWindowFocus: 'always',
  });

  return {
    entertainerCritiqueDataIsReady: isFetched,
    entertainerCritiqueData: data?.critiqueData,
    refetchEntertainerCritiqueData: refetch,
  };
};
useEntertainerCritiqueDataQuery.queryKey = (entertainerId) => [
  'entertainer-critiques',
  entertainerId,
];
useEntertainerCritiqueDataQuery.queryFn = (entertainerId) =>
  API.get<{ critiqueData: CritiqueData }>(`/v1/entertainers/${entertainerId}/critiques`);

export const useCurrentUserEntertainerCritiqueQuery = (entertainerId: string, isEnabled = true) => {
  const { isFetched, data, refetch } = useQuery({
    queryKey: useCurrentUserEntertainerCritiqueQuery.queryKey(entertainerId),
    queryFn: () => useCurrentUserEntertainerCritiqueQuery.queryFn(entertainerId),
    enabled: isEnabled && !!entertainerId,
  });

  return {
    userEntertainerCritiqueIsReady: isFetched,
    userEntertainerCritique: data?.userCritique,
    refetchUserEntertainerCritique: refetch,
  };
};
useCurrentUserEntertainerCritiqueQuery.queryKey = (entertainerId) => [
  'user-critiques',
  'entertainers',
  entertainerId,
];
useCurrentUserEntertainerCritiqueQuery.queryFn = (entertainerId) =>
  API.get<{ userCritique: UserCritique }>(`/v1/user/critiques/entertainers/${entertainerId}`);

export const useCurrentEntertainerBadgesQuery = (entertainerId: string, isEnabled = true) => {
  const { isFetched, data, refetch } = useQuery({
    queryKey: useCurrentEntertainerBadgesQuery.queryKey(entertainerId),
    queryFn: () => useCurrentEntertainerBadgesQuery.queryFn(entertainerId),
    enabled: isEnabled && !!entertainerId,
    refetchOnMount: 'always',
    refetchOnReconnect: 'always',
    refetchOnWindowFocus: 'always',
  });

  return {
    entertainerBadgesAreReady: isFetched,
    sentBadges: data?.sent,
    receivedBadges: data?.received,
    refetchEntertainerBadges: refetch,
  };
};
useCurrentEntertainerBadgesQuery.queryKey = (entertainerId) => [
  'currentEntertainer',
  entertainerId,
  'badges',
];
useCurrentEntertainerBadgesQuery.queryFn = (entertainerId) =>
  API.get<{ sent: EntertainerBadge[]; received: EntertainerBadge[] }>(
    `/v1/user/entertainer/${entertainerId}/badges`,
  );

export const useCurrentEntertainerAvailabilityQuery = (entertainerId: string) => {
  const { isFetched, data, refetch } = useQuery({
    queryKey: useCurrentEntertainerAvailabilityQuery.queryKey(entertainerId),
    queryFn: () => useCurrentEntertainerAvailabilityQuery.queryFn(entertainerId),
    enabled: !!entertainerId,
  });

  return {
    currentEntertainerAvailabilityIsReady: isFetched,
    currentEntertainerAvailability: data?.availability ?? {},
    refetchCurrentEntertainerAvailability: refetch,
  };
};

useCurrentEntertainerAvailabilityQuery.queryKey = (entertainerId: string) => [
  'entertainer-availability',
  entertainerId,
];

useCurrentEntertainerAvailabilityQuery.queryFn = (entertainerId: string) =>
  API.get<{
    availability: Record<string, boolean>;
  }>(`/v1/user/entertainer/${entertainerId}/availability`);

export interface EntertainerSchedule {
  didSubmitThisWeek: boolean;
  weekStreakCount: number;
  dates?: string[];
  timezone?: string;
  updatedAt?: string;
  weekOf?: string;
  dateDetails?: Record<string, { showName: string; venue: string; updatedAt: Date }>;
}

export const useCurrentEntertainerScheduleQuery = () => {
  const { entertainer: currentEntertainer } = useCurrentEntertainerQuery();

  const timezone = guessTimezone();
  const { isFetched, data, refetch } = useQuery({
    queryKey: useCurrentEntertainerScheduleQuery.queryKey(currentEntertainer?._id ?? ''),
    queryFn: () => useCurrentEntertainerScheduleQuery.queryFn(timezone),
  });

  return {
    scheduleIsReady: isFetched,
    schedule: data,
    refetchSchedule: refetch,
  };
};

useCurrentEntertainerScheduleQuery.queryKey = (entertainerId: string) => [
  'user',
  'entertainer',
  entertainerId,
  'schedule',
  entertainerId,
];

useCurrentEntertainerScheduleQuery.queryFn = (timezone: string) =>
  API.get<EntertainerSchedule>(
    `/v1/user/entertainer/schedule?timezone=${encodeURIComponent(timezone)}`,
  );

export const useEntertainerEventRequestsSplashNotificationQuery = (
  options: { skipReferralSplashNotification?: boolean } = {},
) => {
  const { isFetched, data, refetch } = useQuery({
    queryKey: useEntertainerEventRequestsSplashNotificationQuery.queryKey(),
    queryFn: () => useEntertainerEventRequestsSplashNotificationQuery.queryFn(options),
    refetchOnMount: 'always',
    refetchOnReconnect: 'always',
    refetchOnWindowFocus: 'always',
  });

  return {
    entertainerEventRequestsSplashNotificationIsReady: isFetched,
    entertainerEventRequestsSplashNotification: data?.splashNotification,
    refetchEntertainerEventRequestsSplashNotification: refetch,
  };
};

useEntertainerEventRequestsSplashNotificationQuery.queryKey = () => [
  'entertainer-event-requests-splash-notification',
];

useEntertainerEventRequestsSplashNotificationQuery.queryFn = (
  options: { skipReferralSplashNotification?: boolean } = {},
) => {
  const query = options.skipReferralSplashNotification
    ? '?skipReferralSplashNotification=true'
    : '';

  return API.get<{ splashNotification?: SplashNotification }>(
    `/v1/user/entertainer/event-requests/splash-notification${query}`,
  );
};

export const useCityEntertainerAvailabilityQuery = (cityId: string) => {
  const { isFetched, data, refetch } = useQuery({
    queryKey: useCityEntertainerAvailabilityQuery.queryKey(cityId),
    queryFn: () => useCityEntertainerAvailabilityQuery.queryFn(cityId),
    enabled: !!cityId,
  });

  return {
    cityEntertainerAvailabilityIsReady: isFetched,
    cityEntertainerAvailability: data,
    refetchCityEntertainerAvailability: refetch,
  };
};

useCityEntertainerAvailabilityQuery.queryKey = (cityId: string) => [
  'city-entertainer-availability',
  cityId,
];
useCityEntertainerAvailabilityQuery.queryFn = (cityId: string) =>
  API.get<{
    availabilityByDate: Record<string, string[]>;
    entertainersById: Record<string, Entertainer>;
  }>(`/v1/cities/${cityId}/availability`);

export const useEntertainerQuickPicksQuery = (entertainerId: string, firstQuestionId?: string) => {
  const { isFetched, data, refetch } = useQuery({
    queryKey: useEntertainerQuickPicksQuery.queryKey(entertainerId, firstQuestionId),
    queryFn: () => useEntertainerQuickPicksQuery.queryFn(entertainerId, firstQuestionId),
    enabled: !!entertainerId,
  });

  return {
    critiqueQuestionsAreReady: isFetched,
    critiqueQuestions: data?.critiqueQuestions,
    refetchCritiqueQuestions: refetch,
  };
};

useEntertainerQuickPicksQuery.queryKey = (entertainerId: string, firstQuestionId?: string) => [
  'entertainer',
  entertainerId,
  'quick-picks',
  firstQuestionId,
];
useEntertainerQuickPicksQuery.queryFn = (entertainerId: string, firstQuestionId?: string) => {
  const query = firstQuestionId ? `?firstQuestionId=${firstQuestionId}` : '';

  return API.get<{
    critiqueQuestions: CritiqueQuestion[];
  }>(`/v1/entertainers/${entertainerId}/quick-picks${query}`);
};

interface EntertainerStandoutsQueryParams {
  cityId: string;
  type: string;
  badgeId?: string;
  recommendationSlug?: string;
}

export interface StandoutScreenSection {
  title: string;
  entertainerIds: string[];
  countOverride?: number;
  emptyState?: { title: string; text: string };
}

export interface StandoutScreen {
  imgUrl?: string;
  title?: string;
  description?: string;
  sections: StandoutScreenSection[];
}

export const useEntertainersStandoutsQuery = (params: EntertainerStandoutsQueryParams) => {
  const { cityId, type } = params;

  const { isFetched, data, refetch } = useQuery({
    queryKey: useEntertainersStandoutsQuery.queryKey(params),
    queryFn: () => useEntertainersStandoutsQuery.queryFn(params),
    enabled: !!cityId && !!type,
  });

  return {
    entertainerStandoutsAreReady: isFetched,
    entertainerStandouts: data?.standoutsScreen,
    refetchRecommendedEntertainers: refetch,
  };
};

useEntertainersStandoutsQuery.queryKey = ({
  cityId,
  type,
  badgeId,
  recommendationSlug,
}: EntertainerStandoutsQueryParams) => [
  'standout-entertainers',
  type,
  cityId,
  badgeId,
  recommendationSlug,
];

useEntertainersStandoutsQuery.queryFn = ({
  cityId,
  type,
  badgeId,
  recommendationSlug,
}: EntertainerStandoutsQueryParams) => {
  let query = `?type=${type}`;

  if (badgeId) {
    query += `&badgeId=${badgeId}`;
  }

  if (recommendationSlug) {
    query += `&recommendationSlug=${recommendationSlug}`;
  }

  return API.get<{
    standoutsScreen: StandoutScreen;
  }>(`/v1/cities/${cityId}/entertainers/standouts${query}`);
};

export enum EntertainerFilter {
  All = 'all',
  DragKingsThings = 'dragKingsThings',
  Bipoc = 'bipoc',
}
export const useFilteredEntertainerIdsQuery = (
  cityId: string,
  type: string,
  filter: EntertainerFilter,
) => {
  const { isFetched, data, refetch } = useQuery({
    queryKey: useFilteredEntertainerIdsQuery.queryKey(cityId, type, filter),
    queryFn: () => useFilteredEntertainerIdsQuery.queryFn(cityId, type, filter),
    enabled: !!cityId && !!type && !!filter && filter !== EntertainerFilter.All,
  });

  return {
    filteredEntertainerIdsAreReady: isFetched,
    filteredEntertainersTitle: data?.title,
    filteredEntertainerIds: data?.entertainerIds ?? [],
    refetchFilteredEntertainerIds: refetch,
  };
};
useFilteredEntertainerIdsQuery.queryKey = (cityId: string, type: string, filter: string) => [
  'filtered-entertainers',
  type,
  cityId,
  filter,
];
useFilteredEntertainerIdsQuery.queryFn = (cityId: string, type: string, filter: string) =>
  API.get<{
    title: string;
    entertainerIds: string[];
  }>(`/v1/cities/${cityId}/filtered-entertainers/?type=${type}&filter=${filter}`);

export const useFilteredEntertainersQuery = (params: {
  cityId?: string;
  type: string;
  filter: EntertainerFilter;
}) => {
  const { cityId, type, filter } = params;

  const { isFetched, data, refetch } = useQuery({
    queryKey: useFilteredEntertainersQuery.queryKey(cityId, type, filter),
    queryFn: () => useFilteredEntertainersQuery.queryFn(cityId, type, filter),
  });

  return {
    filteredEntertainersAreReady: isFetched,
    filteredEntertainers: data?.entertainers ?? [],
    refetchFilteredEntertainerIds: refetch,
  };
};
useFilteredEntertainersQuery.queryKey = (cityId?: string, type?: string, filter?: string) => [
  'filtered-entertainers-options',
  type,
  cityId,
  filter,
];
useFilteredEntertainersQuery.queryFn = (cityId: string, type: string, filter: string) => {
  const queryParams = new URLSearchParams({ type, filter });

  if (cityId) {
    queryParams.append('cityId', cityId);
  }

  return API.get<{
    entertainers: { title: string; _id: string }[];
  }>(`/v1/filtered-entertainers/?${queryParams.toString()}`);
};

// Copied from entertainer directory
interface DirectorySection {
  title: string;
  imgUrl: string;
  count: number;
  to: string;
}

export const useEntertainerDirectory = (cityId: string) => {
  const { isFetched, data, refetch } = useQuery({
    queryKey: useEntertainerDirectory.queryKey(cityId),
    queryFn: () => useEntertainerDirectory.queryFn(cityId),
    enabled: !!cityId,
  });

  return {
    entertainerDirectoryIdReady: isFetched,
    entertainerDirectory: data,
    refetchEntertainerDirectory: refetch,
  };
};
// Check `useFilteredEntertainerIdsQuery` for future filtering options
useEntertainerDirectory.queryKey = (cityId: string) => ['entertainer-directory', cityId];
useEntertainerDirectory.queryFn = (cityId: string) =>
  API.get<{
    sections: DirectorySection[];
  }>(`/v1/cities/${cityId}/directory`);
